import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  AllOrderReturnResponse,
  DeliveryOrder,
} from "../../../../@types/DRO/GetAllOrderReturnResponse";
import { DeliveryReturnApi } from "../../../../api/DeliveryReturn/DeliveryReturnApi";
import { useLoading } from "../../../../views/Loader/LoadingContext";

interface TableProps {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  search: string;
  statusFilter?: string;
}

interface ReturnOrderDetails {
  id: number;
  doId: number;
  status: string;
  reference: string;
  vendorName: string;
  sourceWarehouseName: string;
  destinationWarehouseName: string;
  deliveryWarehouseName: string;
  creationDate: string;
  returnLines: {
    id: number;
    partId: number;
    partName: string;
    quantityReturned: number;
    returnReason: string;
  }[];
}

const ReturnOrderModal: React.FC<{
  open: boolean;
  onClose: () => void;
  orderId: number | null;
}> = ({ open, onClose, orderId }) => {
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<ReturnOrderDetails | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    }
  }, [open, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DeliveryReturnApi.api.getSingleReturnOrderDetails(
        orderId
      );
      setOrderDetails(response.data);
    } catch (error) {
      console.error("Error fetching return order details:", error);
      setError("Failed to fetch return order details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<
      string,
      "default" | "primary" | "success" | "warning" | "error"
    > = {
      DONE: "success",
      IN_PROGRESS: "warning",
      PENDING: "primary",
      CANCELLED: "error",
    };
    return colorMap[status] || "default";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          pb: 2,
        }}>
        <Typography variant="h6" component="div">
          Return Order Details
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : !orderDetails ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}>
            <Typography color="text.secondary">
              No return order details found
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={4}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                        Order Information
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                        Return Order ID
                      </TableCell>
                      <TableCell>{orderDetails.id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Reference
                      </TableCell>
                      <TableCell>{orderDetails.reference}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Delivery Order ID
                      </TableCell>
                      <TableCell>{orderDetails.doId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell>
                        <Chip
                          label={orderDetails.status}
                          size="small"
                          color={getStatusColor(orderDetails.status)}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Vendor</TableCell>
                      <TableCell>{orderDetails.vendorName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Source Warehouse
                      </TableCell>
                      <TableCell>{orderDetails.sourceWarehouseName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Destination Warehouse
                      </TableCell>
                      <TableCell>
                        {orderDetails.destinationWarehouseName}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Delivery Warehouse
                      </TableCell>
                      <TableCell>
                        {orderDetails.deliveryWarehouseName}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Creation Date
                      </TableCell>
                      <TableCell>{orderDetails.creationDate}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}>
                Return Line Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell>Part ID</TableCell>
                      <TableCell>Part Name</TableCell>
                      <TableCell>Quantity Returned</TableCell>
                      <TableCell>Return Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDetails.returnLines?.length > 0 ? (
                      orderDetails.returnLines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.partId}</TableCell>
                          <TableCell>{line.partName}</TableCell>
                          <TableCell>{line.quantityReturned}</TableCell>
                          <TableCell>{line.returnReason}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No return line items found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TransferOrderReturnTable: React.FC<TableProps> = ({
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  search,
  statusFilter,
}) => {
  const [data, setData] = useState<DeliveryOrder[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const { setLoading } = useLoading();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response: AllOrderReturnResponse =
          await DeliveryReturnApi.api.getReturnedOrderAllList(
            currentPage,
            pageSize,
            search,
            undefined,
            undefined,
            statusFilter || undefined,
            "TRANSFER"
          );

        if (!response.data?.data) {
          throw new Error("Invalid response data");
        }

        setData(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching return orders:", error);
        setData([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage, pageSize, search, statusFilter, setLoading]);

  const getStatusBadge = (
    status: string
  ): { className: string; label: string } => {
    const statusMap: Record<string, { className: string; label: string }> = {
      DONE: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: "Done",
      },
      IN_PROGRESS: {
        className: "bg-yellow-100 text-yellow-800 border border-yellow-300",
        label: "In Progress",
      },
      PENDING: {
        className: "bg-blue-100 text-blue-800 border border-blue-300",
        label: "Pending",
      },
      CANCELLED: {
        className: "bg-red-100 text-red-800 border border-red-300",
        label: "Cancelled",
      },
    };
    return (
      statusMap[status] || {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: status,
      }
    );
  };

  const handleRowClick = (orderId: number) => {
    setSelectedOrderId(orderId);
    setModalOpen(true);
  };

  const columns = [
    { key: "#", label: "#", width: "w-16" },
    { key: "doId", label: "DO ID", width: "w-24" },
    { key: "reference", label: "Reference", width: "w-32" },
    { key: "vendorName", label: "To Vendor", width: "w-48" },
    { key: "sourceWarehouseName", label: "Source Warehouse", width: "w-40" },
    {
      key: "destinationWarehouseName",
      label: "Destination Warehouse",
      width: "w-40",
    },
    {
      key: "deliveryWarehouseName",
      label: "Delivery Warehouse",
      width: "w-40",
    },
    { key: "creationDate", label: "Creation Date", width: "w-40" },
    { key: "status", label: "Status", width: "w-32" },
  ];

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-hidden">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.width} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.length > 0 ? (
              data.map((row, index) => {
                const statusInfo = getStatusBadge(row.status);
                const isHighlighted = row.status === "IN_PROGRESS";

                return (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                      isHighlighted
                        ? "bg-yellow-50 border-l-4 border-l-yellow-500"
                        : ""
                    }`}
                    onClick={() => handleRowClick(row.id)}>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {currentPage * pageSize + index + 1}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      {row.doId}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.reference}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.vendorName}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.sourceWarehouseName}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.destinationWarehouseName}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.deliveryWarehouseName}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {row.creationDate}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns?.length}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No return orders found
                    </p>
                    <p className="text-gray-500">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages - 1))
              }
              disabled={currentPage >= totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}

      {/* Return Order Details Modal */}
      <ReturnOrderModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        orderId={selectedOrderId}
      />
    </div>
  );
};

export default TransferOrderReturnTable;
